import {
  BEST_PRACTICES,
  COORDINATE_FORMATS,
  DATUM_NOTE,
  DATUMS,
  FAILURE_MODES,
  QUALITY_METRICS,
  TRACK_TERMS,
} from "@nav-games/gps";

interface GpsProps {
  onNavigate: (to: string) => void;
}

export function Gps({ onNavigate }: GpsProps) {
  return (
    <>
      <h2 style={{ marginTop: 0 }}>GPS literacy</h2>
      <p className="muted">
        Reading what the chartplotter tells you, and knowing when not
        to believe it. Coordinate formats and datums, the track-vector
        acronyms (COG / SOG / CTS / HDG / XTE), position-quality
        metrics, and the eight failure modes that catch out
        mariners with too much faith in electronics.
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Coordinate formats</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Same point, three notations. The maritime standard is
          degrees + decimal minutes (DDM) — that's what every
          Admiralty chart, almanac, and chartplotter uses by default.
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left" }}>
              <th style={{ padding: "6px 8px 6px 0" }}>Format</th>
              <th style={{ padding: "6px 8px" }}>Example</th>
            </tr>
          </thead>
          <tbody>
            {COORDINATE_FORMATS.map((f) => (
              <tr key={f.name} style={{ verticalAlign: "top" }}>
                <td style={{ padding: "6px 8px 6px 0", width: "30%" }}>
                  <div style={{ fontWeight: 600 }}>{f.name}</div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {f.summary}
                  </div>
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <div
                    style={{
                      fontFamily: "ui-monospace, monospace",
                      fontWeight: 600,
                    }}
                  >
                    {f.example}
                  </div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {f.use}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="small muted" style={{ marginTop: 12 }}>
          50°23.5'N 4°10.7'W (Plymouth Sound) is used as the
          reference point above so the formats are directly
          comparable.
        </p>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Geodetic datums</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          {DATUM_NOTE}
        </p>
        {DATUMS.map((d) => (
          <details key={d.abbreviation} className="card" style={{ marginBottom: 6 }}>
            <summary
              style={{
                cursor: "pointer",
                listStyle: "none",
                fontWeight: 600,
              }}
            >
              {d.abbreviation} — {d.name}
              <div className="small muted" style={{ fontWeight: 400, marginTop: 2 }}>
                {d.region}
              </div>
            </summary>
            <p className="small muted" style={{ margin: "8px 0 4px" }}>
              {d.description}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Shift from WGS84:</strong> {d.shiftFromWgs84}
            </p>
          </details>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Track-vector terminology</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          What the plotter is telling you — and which value to trust
          when they disagree.
        </p>
        <table style={{ width: "100%", fontSize: 14 }}>
          <tbody>
            {TRACK_TERMS.map((t) => (
              <tr key={t.abbreviation} style={{ verticalAlign: "top" }}>
                <td
                  style={{
                    padding: "8px 8px 8px 0",
                    width: 80,
                    fontWeight: 700,
                  }}
                >
                  {t.abbreviation}
                </td>
                <td style={{ padding: "8px" }}>
                  <div style={{ fontWeight: 600 }}>{t.name}</div>
                  <div className="small muted" style={{ marginTop: 2 }}>
                    {t.meaning}
                  </div>
                  <div
                    className="small"
                    style={{
                      marginTop: 4,
                      fontStyle: "italic",
                      color: "var(--text-muted)",
                    }}
                  >
                    {t.example}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Position-quality metrics</h3>
        {QUALITY_METRICS.map((q) => (
          <div
            key={q.abbreviation}
            style={{
              borderTop: "1px solid var(--border)",
              paddingTop: 10,
              marginTop: 10,
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {q.abbreviation} — {q.name}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              {q.description}
            </p>
            <p className="small muted" style={{ margin: "4px 0" }}>
              <strong>Scale:</strong> {q.scale}
            </p>
          </div>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Failure modes</h3>
        <p className="small muted" style={{ marginBottom: 12 }}>
          Eight ways the GPS can mislead you. None of them are
          rare — datum mismatch and stopped-vessel noise catch
          every chartplotter user eventually.
        </p>
        {FAILURE_MODES.map((f) => (
          <details key={f.id} className="card" style={{ marginBottom: 6 }}>
            <summary
              style={{
                cursor: "pointer",
                listStyle: "none",
                fontWeight: 600,
              }}
            >
              {f.title}
            </summary>
            <p className="small muted" style={{ margin: "8px 0" }}>
              {f.description}
            </p>
            <p className="small muted" style={{ margin: "8px 0" }}>
              <strong>Mitigation:</strong> {f.mitigation}
            </p>
          </details>
        ))}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Best practice</h3>
        <ul className="small" style={{ paddingLeft: 18, margin: 0 }}>
          {BEST_PRACTICES.map((p, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              {p}
            </li>
          ))}
        </ul>
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}
